import { NavLink } from 'react-router-dom'

import { useSplitEnabled } from '@buffer-mono/features'
import {
  Combobox,
  EmptyState,
  TagIcon,
  Tag,
  SearchIcon,
  type ButtonProps,
  Button,
  IconButton,
  SettingsIcon,
} from '@buffer-mono/popcorn'

import { type FragmentType, graphql, getFragmentData } from '~publish/gql'
import type { FilterByTag_TagFragment } from '~publish/gql/graphql'
import { settingsTagsRoute } from '~publish/legacy/routes'

import { FilterButton } from '../FilterButton'

import styles from './FilterByTag.module.css'

export const FilterByTag_Tag = graphql(/* GraphQL */ `
  fragment FilterByTag_Tag on Tag {
    id
    name
    color
  }
`)

/**
 * Masked type provided by parent query result.
 * Use `useFilterByTagFragment` to get the unmasked type.
 */
export type MaskedFilterByTagFragment = readonly FragmentType<
  typeof FilterByTag_Tag
>[]

/**
 * Unmasked type extracted from parent query result.
 */
export type FilterByTagFragment = FilterByTag_TagFragment
/**
 * List of unmasked tag filter fragments extracted from parent query result.
 */
export type FilterByTagFragments = readonly FilterByTag_TagFragment[]

/**
 * Unmask tag filters from the parent query result into typed
 *  fragments.
 * @param tags masked tag filter fragments from the parent query result
 * @returns
 */
export const useFilterByTagFragment = (
  tags: MaskedFilterByTagFragment,
): FilterByTagFragments => getFragmentData(FilterByTag_Tag, tags)

type FilterByTagProps = Pick<ButtonProps, 'size' | 'variant'> & {
  tags: MaskedFilterByTagFragment
  /**
   * Selected tag ids
   */
  value: string[]
  onSelect: (tagIds: string[]) => void
}

/**
 * Component to enable filtering by tags, using Combobox
 * It exposes fragment to fetch tags, tags array and selected tags
 */
export const FilterByTag = ({
  tags: passedTags,
  size = 'medium',
  variant = 'tertiary',
  value,
  onSelect,
  ...props
}: FilterByTagProps): JSX.Element => {
  const tags = getFragmentData(FilterByTag_Tag, passedTags)
  const { isEnabled: newTagsPageEnabled } = useSplitEnabled(
    'tags-management-on-settings',
  )

  return (
    <Combobox multiple value={value} onChange={onSelect}>
      <Combobox.Trigger {...props}>
        <FilterButton
          icon={<TagIcon />}
          count={value.length}
          variant={variant}
          size={size}
        >
          Tags
        </FilterButton>
      </Combobox.Trigger>
      <Combobox.Content>
        <Combobox.Input placeholder="Search tags" prefix={<SearchIcon />} />
        <Combobox.List className={styles.list}>
          <Combobox.Empty>
            {tags.length === 0 ? (
              <EmptyState size="small" variant="primary">
                <EmptyState.Icon>
                  <TagIcon />
                </EmptyState.Icon>
                <EmptyState.Heading>No tags</EmptyState.Heading>
                <EmptyState.Description>
                  Use tags to organise your content
                </EmptyState.Description>
              </EmptyState>
            ) : (
              <EmptyState size="small">
                <EmptyState.Icon>
                  <SearchIcon />
                </EmptyState.Icon>
                <EmptyState.Description>No tags found</EmptyState.Description>
              </EmptyState>
            )}
          </Combobox.Empty>
          {tags.map(({ id, name, color }) => (
            <Combobox.Item key={id} value={id} keywords={[name]}>
              <Tag color={color}>{name}</Tag>
            </Combobox.Item>
          ))}
        </Combobox.List>
        {newTagsPageEnabled && (
          <Combobox.Footer>
            <Combobox.Clear>
              <Button variant="tertiary">Clear all</Button>
            </Combobox.Clear>
            <IconButton
              variant="tertiary"
              label="Settings"
              tooltip="Settings"
              as={NavLink}
              to={settingsTagsRoute.route}
            >
              <SettingsIcon />
            </IconButton>
          </Combobox.Footer>
        )}
      </Combobox.Content>
    </Combobox>
  )
}
